import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Api, { exceptionNotificationAPI } from '../../services/api';
import { useAuth } from '../../hooks/auth';

import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import Swal from 'sweetalert2';
import ChatDiscussion from '../Discussion/index';
import { Chat, Discussion } from './styles';
import { ICON_COLORS } from '../ChatButton/colors'

const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== 'string') return `rgba(128, 128, 128, ${alpha})`;
    let h = hex.replace('#', '').trim();
    if (h.length === 3) {
        h = h.split('').map((c) => c + c).join('');
    }
    if (h.length !== 6) return `rgba(128, 128, 128, ${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if (Number.isNaN(r + g + b)) return `rgba(128, 128, 128, ${alpha})`;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const tagPillBaseColor = (color) => color || '#9ca3af';

const normalizeDiscussionTagIdsKey = (tagOptions) =>
    [...(tagOptions || [])]
        .map((o) => o?.value)
        .filter((v) => v != null)
        .sort((a, b) => a - b)
        .join(',');

const discussionTagsToSelectOptions = (discussionTags) =>
    (discussionTags || []).map((dt) => ({
        value: dt.tagID,
        label: dt.tagDescription || dt.tagName,
        color: dt.tagColor,
    }));

/** Add/remove discussion tags: Admin, RoleApprover - Global, RoleReviewer - Global only. */
export const getCanManageAllDiscussionTags = (rolesList, userObj) => {
    const roles = rolesList ?? [];
    if(userObj == null) return false;
    if (roles.includes('RoleAdmin')) return true;
    if (roles.includes('RoleApprover') && userObj.companyID === null || roles.includes('RoleReviewer') && userObj.companyID === null) return true;
    return false;
};

const ChatWindow = ({
    request, show, setShowChat, status, loadDiscussions = null
}) => {

    const emptyChat = {
        isDisabled: false,
        showButtons: false,
        discussions: [],
        currentDiscussionId: null,
        isLastDiscussionOpen: false
    };


    const { userRoles } = useAuth();
    const rolesList = useMemo(() => {
        if (userRoles == null) return [];
        if (Array.isArray(userRoles)) return userRoles;
        if (typeof userRoles === 'string') {
            try {
                const p = JSON.parse(userRoles);
                return Array.isArray(p) ? p : [];
            } catch {
                return [];
            }
        }
        return [];
    }, [userRoles]);

    const [sendingMessage, setSendingMessage] = useState(true);
    const [loadChat, setLoadChat] = useState(false);
    const [chatLoaded, setChatLoaded] = useState(false);
    const [userObject, setUserObject] = useState(JSON.parse(localStorage.getItem('@Thrive:user')));
    const [chat, setChat] = useState({ ...emptyChat });
    const [chatStatus, setChatStatus] = useState("default");
    const [discussionTagOptions, setDiscussionTagOptions] = useState([]);
    const [selectedDiscussionTags, setSelectedDiscussionTags] = useState([]);
    const initialDiscussionTagIdsKeyRef = useRef('');
    const chatRef = useRef(chat);
    const selectedDiscussionTagsRef = useRef([]);
    const discussionTagOptionsRef = useRef([]);
    const restrictedHiddenTagIdsRef = useRef([]);
    const rolesListRef = useRef(rolesList);
    const chatDataChangedRef = useRef(false);

    useEffect(() => {
        chatRef.current = chat;
    }, [chat]);

    useEffect(() => {
        selectedDiscussionTagsRef.current = selectedDiscussionTags;
    }, [selectedDiscussionTags]);

    useEffect(() => {
        discussionTagOptionsRef.current = discussionTagOptions;
    }, [discussionTagOptions]);

    useEffect(() => {
        rolesListRef.current = rolesList;
    }, [rolesList]);

    const selectedTagIdSet = useMemo(
        () => new Set(selectedDiscussionTags.map((t) => t.value)),
        [selectedDiscussionTags],
    );

    const [tagPickerOpen, setTagPickerOpen] = useState(false);
    const tagPickerAnchorRef = useRef(null);

    useEffect(() => {
        if (show == true)
            setLoadChat(!loadChat);
        else if (show == false) {
            setSendingMessage(true);
            setChat({ ...emptyChat });
            setDiscussionTagOptions([]);
            setSelectedDiscussionTags([]);
            setTagPickerOpen(false);
            initialDiscussionTagIdsKeyRef.current = '';
            restrictedHiddenTagIdsRef.current = [];
            chatDataChangedRef.current = false;
        }
    }, [show]);

    useEffect(() => {
        loadData().then(scrollToEnd);
    }, [loadChat]);

    const persistDiscussionTags = useCallback(async (visibleSelected) => {
        const d = chatRef.current;
        if (!d?.currentDiscussionId) return false;

        const listSelected = visibleSelected ?? [];
        const currentKey = normalizeDiscussionTagIdsKey(listSelected);
        if (currentKey === initialDiscussionTagIdsKeyRef.current) return false;

        const canFull = getCanManageAllDiscussionTags(rolesListRef.current, userObject);
        const visibleIds = listSelected.map((o) => o.value).filter((v) => v != null);
        const tagIds = canFull
            ? visibleIds
            : [...new Set([...(restrictedHiddenTagIdsRef.current ?? []), ...visibleIds])];

        try {
            const res = await Api.put('Discussion/tags', {
                discussionID: d.currentDiscussionId,
                tagIds,
            });
            const list = res?.data?.response ?? [];
            const allowedSet = new Set((discussionTagOptionsRef.current ?? []).map((o) => o.value));
            const allOptions = discussionTagsToSelectOptions(list);
            const visibleAfter = canFull
                ? allOptions
                : allOptions.filter((t) => allowedSet.has(t.value));

            if (!canFull) {
                restrictedHiddenTagIdsRef.current = (list || [])
                    .map((dt) => dt.tagID)
                    .filter((id) => id != null && !allowedSet.has(id));
            } else {
                restrictedHiddenTagIdsRef.current = [];
            }

            initialDiscussionTagIdsKeyRef.current = normalizeDiscussionTagIdsKey(visibleAfter);
            setSelectedDiscussionTags(visibleAfter);
            setChat((prev) => {
                if (!prev?.discussions?.length) return prev;
                const next = { ...prev, discussions: [...prev.discussions] };
                next.discussions[0] = { ...next.discussions[0], discussionTags: list };
                return next;
            });
            return true;
        } catch (e) {
            exceptionNotificationAPI(e);
            return false;
        }
    }, []);

    useEffect(() => {
        if (!tagPickerOpen) return undefined;
        const onDocMouseDown = (e) => {
            const el = tagPickerAnchorRef.current;
            if (el && !el.contains(e.target)) {
                setTagPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [tagPickerOpen]);

    const loadDiscussionTags = useCallback(async () => {
        try {
            const tagRes = await Api.get('Tag/getForDiscussion');
            const list = tagRes?.data?.response ?? [];
            const opts = list.map((t) => ({
                value: t.tagID,
                label: t.tagDescription,
                color: t.tagColor,
            }));
            setDiscussionTagOptions(opts);
            return opts;
        } catch (e) {
            exceptionNotificationAPI(e);
            setDiscussionTagOptions([]);
            return [];
        }
    }, []);

    const loadData = async () => {
        setChatLoaded(false);
        if (request.kpiId) {
            var object = chat;

            const tagOptions = await loadDiscussionTags();
            const allowedTagIdSet = new Set(tagOptions.map((o) => o.value));
            const canFullTags = getCanManageAllDiscussionTags(rolesList, userObject);

            await Api.get(`Discussion/get?${buildGetQuery()}`).then(async (result) => {
                if (result && result.data) {
                    object.discussions = result.data.response;
                    if (object.discussions.length > 0) {
                        var lastDiscussion = object.discussions[object.discussions.length - 1];
                        object.currentDiscussionId = lastDiscussion.discussionID;
                        object.isDisabled = lastDiscussion.dateEndMessage != null && !canStartDiscussion();

                        const headerTags = discussionTagsToSelectOptions(lastDiscussion.discussionTags);
                        const visibleHeaderTags = canFullTags
                            ? headerTags
                            : headerTags.filter((t) => allowedTagIdSet.has(t.value));

                        if (!canFullTags) {
                            restrictedHiddenTagIdsRef.current = (lastDiscussion.discussionTags || [])
                                .map((dt) => dt.tagID)
                                .filter((id) => id != null && !allowedTagIdSet.has(id));
                        } else {
                            restrictedHiddenTagIdsRef.current = [];
                        }

                        setSelectedDiscussionTags(visibleHeaderTags);
                        initialDiscussionTagIdsKeyRef.current = normalizeDiscussionTagIdsKey(visibleHeaderTags);

                        setChat(object);
                        setSendingMessage(false);
                        setChatStatus(lastDiscussion.dateEndMessage != null ? "closed" : "open");
                        setChatLoaded(true);
                    }
                }
            }).catch((error) => {
                exceptionNotificationAPI(error);
            });

            if (chat.discussions && chat.discussions.length < 1) {
                if (canStartDiscussion()) {
                    Swal.fire({
                        title: 'Do you want to start a new discussion?',
                        text: "There is no discussion open for this KPI. Do you want to start a new discussion?",
                        icon: 'warning',
                        showDenyButton: true,
                        confirmButtonColor: '#112A61',
                        denyButtonColor: '#B0B0BB',
                        denyButtonText: 'Cancel',
                        confirmButtonText: 'Start new discussion',
                        showCloseButton: true
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            await startNewDiscussion(object);
                            setChatLoaded(true);
                        }
                        else {
                            setShowChat(false);
                        }
                    });
                }
                else {
                    Swal.fire({
                        title: "",
                        text: "There is no discussion open for this KPI.",
                        icon: 'warning',
                        showDenyButton: false,
                        confirmButtonColor: '#112A61',
                        confirmButtonText: 'Ok',
                        showCloseButton: true
                    });
                    setShowChat(false);
                }
            }
        }
    }

    const editMessageDiscussion = async (message) => {
        Api.put(`Discussion/Message/Update`, message).then((result) => {
            if (result && result.data) {
                let chatUpdate = chat
                let discussionUpdate = chat.discussions[0];
                let messageToUpdate = discussionUpdate.messages.find(e => e.messageID == message.messageID);
                let messageToUpdateIndex = discussionUpdate.messages.findIndex(e => e.messageID == message.messageID);
                discussionUpdate.messages[messageToUpdateIndex] = result.data.response
                chatUpdate.discussions[0] = discussionUpdate
                setChat({ ...chatUpdate })
                chatDataChangedRef.current = true;
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }


    const buildGetQuery = () => {
        var query = "";
        if (request.reportYearID != null)
            query += `reportYearID=${request.reportYearID}`

        if (request.companyId != null)
            query += `&&companyId=${request.companyId}`

        if (request.moduleId != null)
            query += `&&moduleId=${request.moduleId}`

        if (request.kpiId != null)
            query += `&&kpiId=${request.kpiId}`

        if (request.cultureTypeID != null)
            query += `&&cultureTypeId=${request.cultureTypeID}`

        /*if (request.fuelTypeId != null)
            query += `&&fuelTypeId=${request.fuelTypeId}`

        if (request.curingBarnID != null)
            query += `&&curingBarnID=${request.curingBarnID}`

        if (request.countryId != null)
            query += `&&countryId=${request.countryId}`*/

        if (request.row != null)
            query += `&&row=${request.row}`

        if (request.column != null)
            query += `&&column=${request.column}`

        if (request.subSectionKPIID != null)
            query += `&&subSectionKPIID=${request.subSectionKPIID}`

        return query;
    }

    const onSendMessagePressed = async () => {
        setSendingMessage(true);
        var input = document.getElementsByClassName("message_input")[0];
        var object = chat;

        var lastDiscussion = object.discussions.find((d) => {
            return d.discussionID == object.currentDiscussionId
        })

        if (lastDiscussion.dateEndMessage != null && canStartDiscussion()) {
            await startNewDiscussion(object);

            setSendingMessage(true);

            lastDiscussion = object.discussions.find((d) => {
                return d.discussionID == object.currentDiscussionId
            })
        }

        var message = {
            "discussionID": chat.currentDiscussionId,
            "text": input.value,
            "cultureTypeID": request.cultureTypeID || null,
            "fuelTypeID": request.fuelTypeId || null,
            "curingBarnID": request.curingBarnID || null,
            "countryID": request.countryId || null,
        };

        message.text = message.text ? message.text.trim() : "";

        if (message.text) {
            await Api.post("Message/send", message).then(async (result) => {
                if (result && result.data) {
                    lastDiscussion.messages.push(result.data.response);
                    setChat(object);
                    chatDataChangedRef.current = true;
                }
            }).catch((error) => {
                exceptionNotificationAPI(error);
                setSendingMessage(false);
            });
        }

        input.value = "";
        setChatStatus("open");
        setSendingMessage(false);
        scrollToEnd();
    }

    const onEndDiscussionPressed = async () => {
        if (canEndDiscussion()) {
            if ((status && (status == 4 || status == 7)) || (request && request.status && (request.status == 4 || request.status == 7))) {
                setSendingMessage(true);
                var input = document.getElementsByClassName("message_input")[0];

                var discussion = {
                    "discussionID": chat.currentDiscussionId,
                    "endMessage": input.value,
                    "cultureTypeID": request.cultureTypeID || null,
                    "fuelTypeID": request.fuelTypeId || null,
                    "curingBarnID": request.curingBarnID || null,
                    "countryID": request.countryId || null,
                }

                discussion.endMessage = discussion.endMessage ? discussion.endMessage.trim() : "Discussion ended without a message.";

                await Api.post("Discussion/end", discussion).then(async (result) => {
                    if (result && result.data) {
                        var object = chat;
                        var lastDiscussion = object.discussions.find((d) => {
                            return d.discussionID == object.currentDiscussionId
                        })
                        lastDiscussion.endMessage = result.data.response.endMessage;
                        lastDiscussion.dateEndMessage = result.data.response.dateEndMessage;

                        setChat(object);
                    }
                }).catch((error) => {
                    exceptionNotificationAPI(error);
                    setSendingMessage(false);
                });

                input.value = "";
                setChatStatus("closed");
                setSendingMessage(false);
                scrollToEnd();
            } else {
                Swal.fire({
                    title: "You cannot close the discussion!",
                    text: "For you to be able to close the discussion, the module must have the status Sent to global approver or Approved.",
                    icon: 'warning',
                    showDenyButton: false,
                    confirmButtonColor: '#112A61',
                    confirmButtonText: 'Ok',
                    showCloseButton: true
                })
            }
        }
    }

    const canStartDiscussion = () => {
        var canStartDiscussion = false;

        canStartDiscussion = rolesList.includes("RoleReviewer") || rolesList.includes("RoleDeploymentTrackerReviewer") ? true : rolesList.includes("RoleApprover") ? userObject.companyID === null : canStartDiscussion;

        return canStartDiscussion;
    }

    const canEndDiscussion = () => {
        var canStartDiscussion = false;

        canStartDiscussion = rolesList.includes("RoleReviewer") || rolesList.includes("RoleDeploymentTrackerReviewer") ? true : rolesList.includes("RoleApprover") ? userObject.companyID === null : canStartDiscussion;

        return canStartDiscussion;
    }

    const startNewDiscussion = async (object) => {
        setSendingMessage(true);

        await Api.post(`Discussion/new`, request).then(async (result) => {
            if (result && result.data) {
                if (result.data.response != null) {
                    object.discussions = [...object.discussions, result.data.response];
                    object.currentDiscussionId = object.discussions[object.discussions.length - 1].discussionID;

                    setChat(object);
                    setSendingMessage(false);
                    setChatStatus("open");
                    setSelectedDiscussionTags([]);
                    initialDiscussionTagIdsKeyRef.current = '';
                    restrictedHiddenTagIdsRef.current = [];
                }
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    const scrollToEnd = async () => {
        const discussionElement = document.querySelector("#discussion");
        if (discussionElement)
            discussionElement.scrollTo(0, discussionElement.scrollHeight);
    }

    const sendOnEnterPressed = async (e) => {
        if (e.charCode === 13 && e.ctrlKey)
            onSendMessagePressed()
    }

    const setChatButtonIconColor = () => {
        var button = document.querySelector(`[id^=discussion-button-icon-` + request.subSectionKPIID
            + "-" + request.column ?? 0
            + "-" + request.row ?? 0
            + "-" + request.cultureTypeID ?? 0
            /*+ "-" + request.fuelTypeID ?? 0
            + "-" + request.curingBarnID ?? 0
            + "-" + request.countryID ?? 0*/ + `]`);
        if (button)
            button.style.color = ICON_COLORS[chatStatus];
        if (chat.discussions[0]?.messages.length > 0) {
            if (chat.discussions[0]?.messages[chat.discussions[0].messages.length - 1].createdBy == userObject.id || chatStatus == "closed") {
                if (button.nextElementSibling != null) {
                    button.nextElementSibling.style.display = 'none';
                }
            }
        }

    }

    const finishCloseChat = () => {
        setShowChat(false);
        if (chatDataChangedRef.current && loadDiscussions != null) {
            loadDiscussions();
        }
        chatDataChangedRef.current = false;
        setChatButtonIconColor();
    };

    const closeChat = () => {
        var input = document.getElementsByClassName("message_input")[0];
        if (input.value != "" && input.value) {
            Swal.fire({
                title: 'Do you want to close discussion?',
                text: "There is message not send. Do you want to close discussion?",
                icon: 'warning',
                showDenyButton: true,
                confirmButtonColor: '#112A61',
                denyButtonColor: '#B0B0BB',
                denyButtonText: 'No',
                confirmButtonText: 'Yes',
                showCloseButton: true
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const changedDiscussionTags = await persistDiscussionTags(selectedDiscussionTagsRef.current ?? []);
                    if (changedDiscussionTags) {
                        chatDataChangedRef.current = true;
                    }
                    finishCloseChat();
                }
            });
        } else {
            (async () => {
                const changedDiscussionTags = await persistDiscussionTags(selectedDiscussionTagsRef.current ?? []);
                if (changedDiscussionTags) {
                    chatDataChangedRef.current = true;
                }
                finishCloseChat();
            })();
        }
    }

    const canManageAllDiscussionTags = getCanManageAllDiscussionTags(rolesList, userObject);

    const removeDiscussionTag = (tagId) => {
        setSelectedDiscussionTags((prev) => prev.filter((t) => t.value !== tagId));
    };

    const addDiscussionTagFromPicker = (opt) => {
        setSelectedDiscussionTags((prev) => {
            if (prev.some((t) => t.value === opt.value)) return prev;
            return [...prev, { value: opt.value, label: opt.label, color: opt.color }];
        });
    };

    return chatLoaded && show &&
        <Modal
            show={show}
            size="lg"
            centered
            className={"chat_modal"}
            onHide={closeChat}
        >
            <ModalBody style={{ padding: "0" }}>
                <Chat className="chat_window">
                    <div className="top_menu">
                        <div className="top_menu_inner" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
                            <div className="title">
                                {chat.discussions.length > 0 ? chat.discussions[0].discussionTitle : "Discussion"}
                            </div>
                            <div
                                className="button-close"
                                style={{ marginLeft: 'auto' }}
                                onClick={closeChat}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && closeChat()}
                            >
                                X
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            background: '#fff',
                            padding: '12px 32px 14px',
                            borderBottom: '1px solid #e5e7eb',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                color: '#9ca3af',
                                marginBottom: 10,
                            }}
                        >
                            TAGS
                        </div>
                        <div
                            ref={tagPickerAnchorRef}
                            style={{
                                position: 'relative',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            {selectedDiscussionTags.map((t) => {
                                const c = tagPillBaseColor(t.color);
                                return (
                                    <span
                                        key={t.value}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '6px 10px',
                                            borderRadius: 999,
                                            border: `1px solid ${c}`,
                                            backgroundColor: hexToRgba(t.color, 0.12),
                                            color: '#1f2937',
                                            fontSize: 13,
                                            maxWidth: '100%',
                                        }}
                                    >
                                        <span
                                            aria-hidden
                                            style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: c,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {t.label}
                                        </span>
                                        {canManageAllDiscussionTags ? (
                                            <button
                                                type="button"
                                                aria-label={`Remove ${t.label}`}
                                                onClick={() => removeDiscussionTag(t.value)}
                                                style={{
                                                    border: 'none',
                                                    background: 'transparent',
                                                    padding: 0,
                                                    marginLeft: 2,
                                                    cursor: 'pointer',
                                                    color: '#9ca3af',
                                                    fontSize: 16,
                                                    lineHeight: 1,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                ×
                                            </button>
                                        ) : null}
                                    </span>
                                );
                            })}
                            {canManageAllDiscussionTags ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setTagPickerOpen((prev) => !prev)}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: '6px 12px',
                                            borderRadius: 999,
                                            border: '1px dashed #c4c8d0',
                                            background: '#fff',
                                            color: '#6b7280',
                                            fontSize: 13,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                                        Add tag
                                    </button>
                                    {tagPickerOpen ? (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 'calc(100% + 6px)',
                                                minWidth: 260,
                                                maxWidth: 360,
                                                maxHeight: 280,
                                                overflowY: 'auto',
                                                background: '#fff',
                                                borderRadius: 10,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                border: '1px solid #e5e7eb',
                                                zIndex: 20,
                                                padding: '6px 0',
                                            }}
                                        >
                                            {discussionTagOptions.filter((opt) => !selectedTagIdSet.has(opt.value)).length === 0 ? (
                                                <div style={{ padding: '12px 14px', fontSize: 13, color: '#6b7280' }}>
                                                    No more tags to add
                                                </div>
                                            ) : null}
                                            {discussionTagOptions
                                                .filter((opt) => !selectedTagIdSet.has(opt.value))
                                                .map((opt) => {
                                                    const dot = tagPillBaseColor(opt.color);
                                                    return (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            onClick={() => addDiscussionTagFromPicker(opt)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: '100%',
                                                                gap: 10,
                                                                padding: '10px 14px',
                                                                border: 'none',
                                                                background: '#fff',
                                                                cursor: 'pointer',
                                                                textAlign: 'left',
                                                                fontSize: 14,
                                                                color: '#111827',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = '#f3f4f6';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = '#fff';
                                                            }}
                                                        >
                                                            <span
                                                                aria-hidden
                                                                style={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: dot,
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {opt.label}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    </div>
                    <Discussion id="discussion" style={{ height: 430 }}>
                        {!sendingMessage && chat.discussions.map((discussion) => {
                            return <ChatDiscussion discussion={discussion} currentUser={userObject.id} userObject={userObject} editMessage={editMessageDiscussion} />;
                        })}
                    </Discussion>
                    <div class="bottom_wrapper clearfix">
                        <div class="message_input_wrapper">
                            <textarea class="message_input" placeholder="Type your message here..." disabled={chat.isDisabled} row="4" onKeyPress={sendOnEnterPressed} />
                        </div>
                        <div class="button_wrapper">
                            <button class="send_message" onClick={onSendMessagePressed} disabled={chat.isDisabled} title="Ctrl + Enter">
                                <div class="text">Send</div>
                            </button>
                            {canEndDiscussion() && <div class="end_discussion" onClick={onEndDiscussionPressed} disabled={chat.isDisabled}>
                                <div class="text" >End Discussion</div>
                            </div>}
                        </div>
                    </div>
                </Chat>
            </ModalBody>
        </Modal>

}

export default ChatWindow;
